/* eslint-disable */
import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import Image from 'Shared/Image';
import { formatAmount, getStocks } from 'Utils/ulilities';
import { applyRemise } from 'Components/Catalog/Available';
import noImage from '../../Images/no_image.svg';

/**
 * Shared ProductCard
 * Props:
 *   product      {object}   — product data
 *   onAddToCart  {function} — called with product when "+" is clicked
 *   onClick      {function} — called with product when card body is clicked (optional)
 */
const ProductCard = ({ product, onAddToCart, onClick, hidePrice }) => {
  const {
    name, remise, remiseExceptionnelle, brand, image,
    family, reference, price,
    stockBouskoura, stockAinSebaa, stockDerbOmar, stockCfcim,
    reserved, reservedDO,
  } = product;

  const { stockDisponibleBouskoura, stockDisponibleDebOmar } = getStocks(
    stockDerbOmar, reservedDO, stockBouskoura, stockAinSebaa, stockCfcim, reserved,
  );
  const isAvailable = stockDisponibleBouskoura > 0 || stockDisponibleDebOmar > 0;
  const discountPct = applyRemise(remise, remiseExceptionnelle);
  const discountedPrice = remiseExceptionnelle
    ? Number((price * (100 - discountPct)) / 100)
    : null;

  return (
    <Box
      onClick={() => onClick && onClick(product)}
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1.5px solid #D1D5DC',
        borderRadius: '12px',
        boxShadow: '0px 6px 26px #626B7D12',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: hidePrice ? '370px' : '422px',
        width: '100%',
        position: 'relative',
        transition: 'box-shadow 0.18s, border-color 0.18s',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0px 10px 32px #626B7D22',
          borderColor: '#2557A7',
        },
      }}
    >
      {/* Discount badge */}
      {discountPct > 0 && (
        <Box sx={{
          position: 'absolute', top: 10, left: 10,
          backgroundColor: '#DEFAF3', color: '#0AD69F',
          borderRadius: '30px', px: '7px', py: '2px',
          fontSize: '11px', fontWeight: 800, zIndex: 1,
        }}>
          -{discountPct}%
        </Box>
      )}

      {/* Product image */}
      <Box sx={{
        height: 228, width: '100%',
        backgroundColor: '#FFFFFF',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Image
          src={image || noImage}
          alt={name}
          style={{ height: '100%', width: '100%', objectFit: image ? 'cover' : 'contain', padding: image ? 0 : '20px' }}
        />
        {brand?.logoImage && (
          <Box sx={{ position: 'absolute', bottom: 8, right: 10, overflow: 'hidden', maxWidth: '55%' }}>
            <Image
              src={brand.logoImage}
              alt={brand.name}
              style={{ height: 22, width: 'auto', display: 'block' }}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ height: '1.5px', backgroundColor: '#D1D5DC', mx: 0 }} />

      {/* Card body */}
      <Box sx={{ p: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
        {/* Brand + Availability */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{
            fontSize: '11px', fontWeight: 700, color: '#385576',
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            {brand?.name || ''}
          </Typography>
          {!hidePrice && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%',
                backgroundColor: isAvailable ? '#0AD69F' : '#EF456E', flexShrink: 0,
              }} />
              <Typography sx={{
                fontSize: '11px', fontWeight: 700,
                color: isAvailable ? '#0AD69F' : '#EF456E',
                textTransform: 'uppercase', letterSpacing: '0.3px',
              }}>
                {isAvailable ? 'Disponible' : 'Non Disponible'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Product name — fixed to exactly 2 lines */}
        <Typography sx={{
          fontSize: '13px', fontWeight: 700, color: '#000000', lineHeight: 1.5,
          display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden',
          height: '39px', flexShrink: 0,
        }}>
          {name}
        </Typography>

        {/* Reference */}
        <Typography sx={{ fontSize: '13px', fontWeight: 500, mt: 'auto' }}>
          <Box component="span" sx={{ color: '#385576' }}>Réf :</Box>{' '}
          <Box component="span" sx={{ color: '#007BC6' }}>{reference}</Box>
        </Typography>

        {/* Family */}
        {family?.name && (
          <Typography sx={{
            fontSize: '11px', fontWeight: 500, color: '#717182',
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            {family.name}
          </Typography>
        )}

        {/* Price + add button */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mt: '6px', gap: '8px' }}>
          {!hidePrice && (
            <Box>
              <Typography sx={{ fontSize: '21px', fontWeight: 700, color: '#007BC6', lineHeight: 1.1 }}>
                {`${formatAmount(discountedPrice !== null ? discountedPrice : price, 2)} Dh`}
              </Typography>
              {discountedPrice !== null && (
                <Typography sx={{
                  fontSize: '13px', color: '#717182', fontWeight: 700, textDecoration: 'line-through', mt: '2px',
                }}>
                  {`${formatAmount(price)} Dh`}
                </Typography>
              )}
            </Box>
          )}

          {onAddToCart && (
            <Tooltip title="Ajouter au panier">
              <Box
                onClick={(e) => { e.stopPropagation(); onAddToCart(product); }}
                sx={{
                  width: 34, height: 34,
                  backgroundColor: '#F0F4FA', border: '1px solid #DEE5EE',
                  borderRadius: '8px', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', fontSize: '18px', color: '#2557A7', fontWeight: 700,
                  '&:hover': { backgroundColor: '#2557A7', color: '#fff', borderColor: '#2557A7' },
                }}
              >
                +
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProductCard;
